import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { usePendingRedemption } from '@/contexts/PendingRedemptionContext';

interface PendingRedemptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PendingRedemptionModal = ({ isOpen, onClose }: PendingRedemptionModalProps) => {
  const { 
    pendingRedemptions, 
    totalPendingAmount, 
    cancelRedemption, 
    isCancelling,
    isLowBalanceTrigger,
  } = usePendingRedemption();
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const formatAmount = (amount: number) => {
    return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleCancel = async (requestId: string) => {
    setCancellingId(requestId);
    const success = await cancelRedemption(requestId);
    if (success && pendingRedemptions.length <= 1) {
      onClose();
    }
    setCancellingId(null);
  };

  const hasMultiple = pendingRedemptions.length > 1;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="max-w-[360px] w-[92vw] p-0 border-0 bg-transparent overflow-hidden [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Main card with gradient background */}
        <div 
          className="relative rounded-3xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            boxShadow: '0 0 60px rgba(245, 158, 11, 0.15), 0 25px 50px rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
          }}
        >
          {/* Glowing accent at top */}
          <div 
            className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(245, 158, 11, 0.3) 0%, transparent 70%)' }}
          />

          <div className="relative z-10 p-6 pt-8">
            {/* Title */}
            <h3 className="text-2xl font-black text-white text-center mb-2">
              {isLowBalanceTrigger ? 'NEED MORE SC?' : 'PENDING REDEMPTION'}
            </h3>
            
            {/* Subtitle */}
            <p className="text-white/60 text-sm text-center mb-6">
              {isLowBalanceTrigger 
                ? 'Get your SC back instantly and keep playing!'
                : 'Your redemption is being processed'
              }
            </p>

{/* Amount Card */}
            <div
              className="rounded-xl p-3 mb-4 text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.1) 100%)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
              }}
            >
              <div className="text-[10px] text-amber-400/80 font-semibold mb-1 uppercase tracking-wider">
                {hasMultiple ? 'Total Pending' : 'Pending Amount'}
              </div>
              <div className="flex items-center justify-center gap-1.5">
                <span
                  className="text-2xl font-black"
                  style={{
                    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {formatAmount(totalPendingAmount)}
                </span>
                <span className="text-lg font-bold text-amber-400">SC</span>
              </div>
              {hasMultiple && (
                <div className="text-[10px] text-white/40 mt-1">
                  across {pendingRedemptions.length} requests
                </div>
              )}
            </div>

            {/* Single redemption view */}
            {!hasMultiple && pendingRedemptions[0] && (
              <>
                {/* Info text */}
                <p className="text-white/50 text-xs text-center mb-5 leading-relaxed">
                  {isLowBalanceTrigger ? (
                    <>Cancel to add <span className="text-amber-400 font-semibold">{formatAmount(totalPendingAmount)} SC</span> back to your balance instantly</>
                  ) : (
                    <>This SC is reserved for your redemption. Cancel anytime to keep playing.</>
                  )}
                </p>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={() => handleCancel(String(pendingRedemptions[0].id))}
                    disabled={isCancelling}
                    className="w-full min-h-[56px] font-bold text-lg rounded-2xl transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
                      color: 'white',
                    }}
                  >
                    {cancellingId ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Cancelling...
                      </>
                    ) : (
                      `Cancel & Get ${formatAmount(totalPendingAmount)} SC`
                    )}
                  </button>

                  <button
                    onClick={onClose}
                    disabled={isCancelling}
                    className="w-full min-h-[48px] bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white font-medium rounded-2xl transition-all active:scale-[0.98]"
                  >
                    {isLowBalanceTrigger ? 'Keep Waiting' : 'Got It'}
                  </button>
                </div>
              </>
            )}

            {/* Multiple redemptions view */}
            {hasMultiple && (
              <>
                {/* Redemption list */}
                <div className="space-y-2 mb-5 max-h-[180px] overflow-y-auto">
                  {pendingRedemptions.map((redemption) => {
                    const amount = parseFloat(String(redemption.amount)) || 0;
                    const isThisCancelling = cancellingId === String(redemption.id);
                    return (
                      <div
                        key={redemption.id}
                        className="flex items-center justify-between p-3 rounded-xl"
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                      >
                        <div>
                          <div className="text-white font-bold">{formatAmount(amount)} SC</div>
                          <div className="text-amber-400/60 text-xs">Processing</div>
                        </div>
                        <button
                          onClick={() => handleCancel(String(redemption.id))}
                          disabled={isCancelling}
                          className="px-3 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-50"
                          style={{
                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.15) 100%)',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                            color: '#34d399',
                          }}
                        >
                          {isThisCancelling ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            `Cancel & Get ${formatAmount(amount)} SC`
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Got It button */}
                <button
                  onClick={onClose}
                  disabled={isCancelling}
                  className="w-full min-h-[56px] font-bold text-lg rounded-2xl transition-all active:scale-[0.98] disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
                    color: 'white',
                  }}
                >
                  Got It
                </button>
              </>
            )}

            {/* Fine print */}
            <p className="text-[10px] text-white/30 text-center mt-4">
              SC is credited back instantly when cancelled
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
